import React, { useEffect, useState } from 'react';
import { Drawer, Empty, Spin } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import type { ClaimInsightSummaryVo, TBillingApiScope, WeeklyReportVo } from 'types/insuranceBilling';
import { getBillingClaimInsight, getBillingWeeklyReport } from 'api/insuranceBilling';
import { formatMoney, utcOf } from './statusConfig';
import s from './s.module.less';

type Props = {
    // 周一 YYYY-MM-DD；null 时关闭
    weekStart: string | null;
    apiScope: TBillingApiScope;
    onClose: () => void;
};

const ReportDrawer = ({ weekStart, apiScope, onClose }: Props) => {
    const [report, setReport] = useState<WeeklyReportVo | null>(null);
    const [loading, setLoading] = useState(false);
    // insight 上游走 agent skill 较慢且可能失败 → 独立 loading，失败/空按「暂无洞察」隐藏区块
    const [insights, setInsights] = useState<string[]>([]);
    const [insightLoading, setInsightLoading] = useState(false);

    useEffect(() => {
        if (!weekStart) {
            setReport(null);
            setInsights([]);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setInsightLoading(true);
        (async () => {
            const res: any = await getBillingWeeklyReport({ weekStart }, apiScope);
            if (cancelled) return;
            setReport((!res?.error && res?.data?.data) || null);
            setLoading(false);
        })();
        (async () => {
            const weekEnd = utcOf(weekStart).add(6, 'day').format('YYYY-MM-DD');
            const res: any = await getBillingClaimInsight({ from: weekStart, to: weekEnd }, apiScope);
            if (cancelled) return;
            const vo: ClaimInsightSummaryVo | null = (!res?.error && res?.data?.data) || null;
            setInsights(vo?.summaries || []);
            setInsightLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [weekStart, apiScope]);

    const rangeText = report
        ? `${utcOf(report.weekStart).format('MMM D')} to ${utcOf(report.weekEnd).format('MMM D, YYYY')}`
        : '';

    const summaryCards = report
        ? [
              { label: 'Claims Submitted', value: String(report.summary.claimsSubmitted), sub: 'This week' },
              { label: 'Total Billed', value: formatMoney(report.summary.totalBilled), sub: 'Across all payers' },
              {
                  label: 'Paid / Collected',
                  value: formatMoney(report.summary.paidCollected),
                  sub: report.summary.paidClaims != null ? `${report.summary.paidClaims} claims paid` : 'This week',
              },
              {
                  label: 'Clean Claim Rate',
                  value: `${report.summary.cleanClaimRate}%`,
                  sub:
                      report.summary.cleanClaims != null
                          ? `${report.summary.cleanClaims} of ${report.summary.claimsSubmitted} clean`
                          : 'This week',
              },
          ]
        : [];

    return (
        <Drawer
            open={!!weekStart}
            onClose={onClose}
            width={480}
            title={
                <div>
                    <div className={s.drawerTitle}>Weekly Billing Report</div>
                    {!!rangeText && <div className={s.repRange}>{rangeText}</div>}
                </div>
            }
        >
            {loading && (
                <div className={s.drawerLoading}>
                    <Spin />
                </div>
            )}
            {!loading && !report && !!weekStart && <Empty description="No report for this week yet" />}
            {!loading && report && (
                <div>
                    <div className={s.repSec}>SUMMARY</div>
                    <div className={s.repSumGrid}>
                        {summaryCards.map((c) => (
                            <div key={c.label} className={s.repSumCard}>
                                <div className={s.repSumLabel}>{c.label}</div>
                                <div className={s.repSumVal}>{c.value}</div>
                                {!!c.sub && <div className={s.repSumSub}>{c.sub}</div>}
                            </div>
                        ))}
                    </div>

                    <div className={s.repSec}>PAYER BREAKDOWN</div>
                    <div className={s.repPayers}>
                        {report.payerBreakdown.map((p) => (
                            <div key={p.payer} className={s.repPayer}>
                                <div>
                                    <div className={s.repPayerName}>{p.payer}</div>
                                    <div className={s.repPayerSub}>
                                        {p.claims} claim{p.claims > 1 ? 's' : ''} · {formatMoney(p.billed)} billed
                                    </div>
                                </div>
                                <div className={s.repPayerRight}>
                                    <div className={s.repPayerVal}>{formatMoney(p.collected)}</div>
                                    <div className={s.repPayerSub}>{p.collectionRate}% collected</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* insight 慢于周报很常见：先出周报，洞察区自己转圈；失败或为空整块隐藏 */}
                    {insightLoading && (
                        <>
                            <div className={s.repSec}>KLARITY&apos;S INSIGHTS</div>
                            <div className={s.drawerLoading}>
                                <Spin size="small" />
                            </div>
                        </>
                    )}
                    {!insightLoading && insights.length > 0 && (
                        <>
                            <div className={s.repSec}>KLARITY&apos;S INSIGHTS</div>
                            <div className={s.repInsights}>
                                {insights.map((text) => (
                                    <div key={text} className={s.repInsight}>
                                        <BarChartOutlined style={{ color: '#0e62f4' }} />
                                        <span>{text}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </Drawer>
    );
};

export default ReportDrawer;
